import $ from '../../../../../../FrontendBundle/Resources/js/jquery';
import { Logger, Matrix } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import Layer from './layer';

/**
 * Layer manager
 *
 * @class LayerManager
 */
export default class LayerManager {
    /**
     * @type {number}
     */
    zoom = 0;

    /**
     * @type {number}
     */
    panX = 0;

    /**
     * @type {number}
     */
    panY = 0;

    /**
     * @type {SVGWrapper}
     */
    svg = null;

    /**
     * @type {Map.<string, Layer>}
     */
    rootLayers = new Map();

    /**
     * @type {Layer[]}
     */
    transformableLayers = [];

    /**
     * Initializes the layer manager
     *
     * @constructor
     * @param {SVGWrapper} svg SVG Wrapper
     */
    constructor(svg) {
        this.svg = svg;
        this.svgRoot = this.svg.root();

        this.createTransformingLayer('items');
        const itemsOverlayLayer = this.createTransformingLayer('items-overlay');
        this.createLayer('ui');

        itemsOverlayLayer.getElement().css('pointer-events', 'none');
    }

    /**
     * Bind to canvas
     *
     * @param {Ofcs.Editor.Canvas} canvas Canvas
     */
    setCanvas(canvas) {
        this.zoom = canvas.getZoom();
        this.panX = canvas.getPan().x;
        this.panY = canvas.getPan().y;

        canvas.on('panchange', this.onPanChange, this);
        canvas.on('zoomchange', this.onZoomChange, this);

        this.applyLayerTransform();
    }

    /**
     * Bind to canvas
     *
     * @param {Ofcs.Editor.Canvas} canvas Canvas
     * @param {number} panX Pan X
     * @param {number} panY Pan Y
     * @private
     */
    onPanChange(canvas, panX, panY) {
        this.panX = panX;
        this.panY = panY;

        this.applyLayerTransform();
    }

    /**
     * Bind to canvas
     *
     * @param {Ofcs.Editor.Canvas} canvas Canvas
     * @param {number} zoom Zoom
     * @private
     */
    onZoomChange(canvas, zoom) {
        this.zoom = zoom;

        this.applyLayerTransform();
    }

    /**
     * @private
     */
    applyLayerTransform() {
        const transform = new Matrix()
            .scale(this.zoom)
            .translate(this.panX, this.panY)
            .toString();

        this.transformableLayers.forEach(layer => layer.getElement().attr('transform', transform));
    }

    /**
     * Create DOM element layer that inherits transformation
     *
     * @param {string} name Layer name
     * @param {Object} options Options
     * @param {Layer} parentLayer Parent Layer
     * @return {Layer}
     */
    createTransformingLayer(name, options, parentLayer = null) {
        return this.createLayer(name, options, true, parentLayer);
    }

    /**
     * Removes a given layer only iff it is a direct
     * child of the root svg.
     *
     * @param {string} name Layer name
     * @return {boolean}
     */
    removeRootLayer(name) {
        const element = $(this.svgRoot).children(`[class*=${name}]`);

        if (element.length) {
            element.remove();
            this.rootLayers.delete(name);
            return true;
        }

        return false;
    }

    /**
     * Create DOM element layer, only return if it already exists
     *
     * @param {string} name Layer name
     * @param {Object} options Options
     * @param {boolean} applyLayerTransform Register layer for transforms
     * @param {Layer} parentLayer Parent layer
     * @return {Layer}
     */
    createOrGetLayer(name, options, applyLayerTransform, parentLayer) {
        if (this.hasLayer(name)) {
            return this.getLayer(name);
        }

        return this.createLayer(name, options, applyLayerTransform, parentLayer);
    }

    /**
     * Create DOM element layer
     *
     * @param {string} name Layer name
     * @param {Object} optionsParam Options
     * @param {boolean} applyLayerTransform Register layer for transforms
     * @param {Layer|string} parentLayer Parent layer
     * @return {Layer}
     */
    createLayer(name, optionsParam = {}, applyLayerTransform = false, parentLayer = null) {
        // if layer already exists and is direct child of root svg
        // it will be removed in order to be recreated in an updated stacking order
        if (this.hasLayer(name) && !this.removeRootLayer(name)) {
            throw new Error(`Layer ${name} already exists.`);
        }

        const options = optionsParam || {};

        const parentEl = parentLayer instanceof Layer ? parentLayer.getElement() : this.svgRoot;
        const parentName = parentLayer instanceof Layer ? parentLayer.getName() : '';

        if (!options.transform && applyLayerTransform) {
            const matrix = new Matrix()
                .scale(this.zoom)
                .translate(this.panX, this.panY);

            options.transform = matrix.toString();
        }

        const dom = this.svg.group(parentEl, options);
        const element = $(dom).addClass(`layer-${name}`);

        const layer = new Layer(name, this.svg, dom, element);

        if (parentLayer) {
            parentLayer.setChildLayer(layer);
        } else {
            this.rootLayers.set(name, layer);
        }

        if (applyLayerTransform) {
            this.transformableLayers.push(layer);
        }

        Logger.log('layerManager.createLayer()', parentName ? `${parentName} => ` : '', name);

        return layer;
    }

    /**
     * Returns requested layer
     *
     * @param {string} name Layer name
     * @return {boolean}
     */
    hasLayer(name) {
        return this.rootLayers.has(name);
    }

    /**
     * Returns requested layer
     *
     * @param {string} name Layer name
     * @return {Layer}
     */
    getLayer(name) {
        const namePath = name.split('.');
        const rootLayerName = namePath.shift();

        if (!this.hasLayer(rootLayerName)) {
            throw new Error(`Unknown layer '${rootLayerName}'`);
        }

        const rootLayer = this.rootLayers.get(rootLayerName);
        return namePath.reduce((currentLayer, nextLayerName) => currentLayer.getChildLayer(nextLayerName), rootLayer);
    }

    createItemLayer(item, options = {}) {
        const name = `item-${item.get('type')}-${item.cid}`;
        const itemsLayer = this.createOrGetLayer('items', null, true);

        return this.createLayer(name, options, false, itemsLayer);
    }

    getLayerByItem(item) {
        const name = `item-${item.get('type')}-${item.cid}`;
        const itemsLayer = this.createOrGetLayer('items');
        const itemLayer = itemsLayer.findChildLayer(name);

        if (itemLayer) return itemLayer;

        if (!item.hasParentItem()) {
            throw new Error(`No layer found for item ${item.cid}`);
        }

        return this.getLayerByItem(item.getParentItem());
    }

    removeItemLayer(item) {
        const name = `item-${item.get('type')}-${item.cid}`;
        const itemsLayer = this.createOrGetLayer('items');

        itemsLayer.removeChildLayer(name);
    }

    createItemsOverlayLayer(name, options = {}) {
        return this.createLayer(name, options, false, this.getLayer('items-overlay'));
    }

    createUiLayer(name, options = {}) {
        return this.createLayer(name, options, false, this.getLayer('ui'));
    }

    /**
     * Returns jQuery DOM element for requested layer
     *
     * @param {string} name Layer name
     * @return {jQuery}
     */
    getElement(name) {
        return this.getLayer(name).getElement();
    }
}
